import { useCallback, MouseEvent } from 'react';
import { useIsMobile } from '../../../../../utils/getIsMobile';
import { useExperimentFlag } from '../../../../../utils/useExperimentFlag';
import { IconBookmarkAction } from '../../../../icon/IconBookmarkAction';
import { IconBookmarkActionFilled } from '../../../../icon/IconBookmarkActionFilled';
import { IconCalendarWithClock } from '../../../../icon/IconCalendarWithClock';
import { IconLikeAction } from '../../../../icon/IconLikeAction';
import { IconLikeFilled } from '../../../../icon/IconLikeFilled';
import { Typography } from '../../../../typography/Typography';
import { isPostStatusValidForSave } from '../../../PostDetailsCard/parts/utils/isPostStatusValidForSave';
import { ReactionState } from '../../../types';
import { ViewsBadge } from '../ViewsBadge/ViewsBadge';
import {
    ActionsWrapper,
    Button,
    CardActionsWrapper,
    TextWrapper,
    LeftBadgesWrapper,
    BookingButton,
} from './styles';
import { CardActionsProps } from './types';

export const CardActions: React.FC<CardActionsProps> = ({
    post,
    index,
    onReaction,
    onBookmark,
    ...other
}) => {
    const { text, reactionState, saved, publicStats, business } = post;
    const isMobile = useIsMobile();
    const disableServiceBooking = Number(useExperimentFlag('disable_service_booking')) === 1;
    const isViewsBadgeEnabled = Number(useExperimentFlag('show_views_badge')) === 1;
    const hasBookingBadge = !disableServiceBooking && business?.isDikidiApproved;

    const withCheckout =
        Number(useExperimentFlag('products_checkout')) === 2 &&
        post.products.items.some((product) => product.checkout?.url) &&
        !hasBookingBadge;

    const hasViewsBadge = !withCheckout && isViewsBadgeEnabled && publicStats?.shows;

    const onLikeClick = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();

            onReaction({ postNumber: index, reactionState: ReactionState.LIKE });
        },
        [onReaction, index],
    );

    const onBookmarkClick = useCallback(
        (e: MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();

            onBookmark?.();
        },
        [onBookmark],
    );

    return (
        <CardActionsWrapper {...other} isMobile={isMobile}>
            {hasViewsBadge || hasBookingBadge ? (
                <LeftBadgesWrapper>
                    {hasBookingBadge ? (
                        <BookingButton>
                            <IconCalendarWithClock fontSize={20} color="var(--always-white)" />
                        </BookingButton>
                    ) : null}
                    {hasViewsBadge ? (
                        <ViewsBadge
                            views={publicStats?.shows || 0}
                            background="var(--content-overlay-medium)"
                        />
                    ) : null}
                </LeftBadgesWrapper>
            ) : null}
            {text ? (
                <TextWrapper mb={withCheckout ? 5.5 : undefined}>
                    <Typography
                        variant="caption"
                        color="var(--always-white)"
                        width="100%"
                        lines={3}
                    >
                        {text}
                    </Typography>
                </TextWrapper>
            ) : null}
            <ActionsWrapper>
                <Button onClick={onLikeClick}>
                    {reactionState === 'like' ? (
                        <IconLikeFilled fontSize={16} color="var(--always-white)" />
                    ) : (
                        <IconLikeAction fontSize={16} color="var(--always-white)" />
                    )}
                </Button>
                {isPostStatusValidForSave(post.status) && !post.ownersData?.isOwner ? (
                    <Button onClick={onBookmarkClick}>
                        {saved ? (
                            <IconBookmarkActionFilled fontSize={16} color="var(--always-white)" />
                        ) : (
                            <IconBookmarkAction fontSize={16} color="var(--always-white)" />
                        )}
                    </Button>
                ) : null}
            </ActionsWrapper>
        </CardActionsWrapper>
    );
};
